"use client";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { useTranslation } from "react-i18next";

import AppLayout from "../../../navigation/Layout/Layout";
import AskQuestionBox from "../../components/MedicalQuestionsPage/AskQuestionBox/AskQuestionBox";
import QuestionsList from "../../components/MedicalQuestionsPage/QuestionsList/QuestionsList";
import FollowedDoctors from "../../../DoctorFinder/components/FollowedDoctors/FollowedDoctors";
import AdminConfirmationModal from "../../components/FeedPage/AdminConfirmationModal/AdminConfirmationModal";
import MedilinkInfoCard from "../../components/FeedPage/MedilinkInfoCard/MedilinkInfoCard";
import ScrollTabs from "../../../Profile/components/ScrollTabs/ScrollTabs";

import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import useUserPosts from "../../hooks/useUserPosts";
import usePosts from "../../hooks/usePosts";
import { deletePostByCustomId } from "../../redux/thunks/moderationThunks";
import { selectEffectiveUserId } from "../../../sous-profile/redux/selectors/sousProfileSelectors";

import "./MedicalQuestions.css";

/* -------------------------------------------------------------------------- */
/* 🧭 Détermination de l'onglet actif                                         */
/* -------------------------------------------------------------------------- */
function getActiveKey(pathname) {
  const p = (pathname || "").toLowerCase();
  if (p.startsWith(ROUTES.MEDICAL_QUESTIONS?.toLowerCase()))
    return ROUTES.MEDICAL_QUESTIONS;
  if (p.startsWith(ROUTES.OVERVIEWS?.toLowerCase())) return ROUTES.OVERVIEWS;
  if (p.startsWith(ROUTES.MEDICAL_RECORD?.toLowerCase()))
    return ROUTES.MEDICAL_RECORD;
  return ROUTES.PROFILE;
}

/* -------------------------------------------------------------------------- */
/* 🏥 Page : Medical Questions (Questions personnelles)                       */
/* -------------------------------------------------------------------------- */
export default function MedicalQuestionsPage() {
  const { t } = useTranslation();

  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const activeKey = useMemo(() => getActiveKey(pathname), [pathname]);

  const currentUserId = useSelector(selectEffectiveUserId);

  const { posts, loading, refresh } = useUserPosts(currentUserId);
  const { handleCreatePost, handleEditPost } = usePosts();

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const lastUserIdRef = useRef(null);
  const isInitializedRef = useRef(false);
  const handledTargetRef = useRef(null);

  /* ---------------------------------------------------------------------- */
  /* 🧩 Initialisation unique au montage                                    */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (currentUserId && !isInitializedRef.current) {
      lastUserIdRef.current = currentUserId;
      isInitializedRef.current = true;
      refresh?.();
    } else if (currentUserId && lastUserIdRef.current !== currentUserId) {
      lastUserIdRef.current = currentUserId;
      refresh?.();
    }
  }, [currentUserId, refresh]);

  useEffect(() => {
    const postId = searchParams.get("postId");
    const commentId = searchParams.get("commentId");
    const targetKey = `${postId || ""}:${commentId || ""}`;
    if (!postId || loading || handledTargetRef.current === targetKey) return;

    handledTargetRef.current = targetKey;
    const postExists = (posts || []).some(
      (post) =>
        String(post?.id_post || post?.postId || post?._id || post?.id || "") ===
        String(postId)
    );
    if (!postExists) {
      message.info("This post is no longer available.");
      return;
    }

    const timer = window.setTimeout(() => {
      const postElement = document.getElementById(`post-${postId}`);
      const commentElement = commentId
        ? document.getElementById(`comment-${commentId}`)
        : null;
      if (commentId && !commentElement) {
        message.info("This comment is no longer available.");
      }
      const target = commentElement || postElement;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (!target) message.info("This post is no longer available.");
    }, 350);
    return () => window.clearTimeout(timer);
  }, [loading, posts, searchParams]);

  /* ---------------------------------------------------------------------- */
  /* 🧩 Création d'une question                                             */
  /* ---------------------------------------------------------------------- */
  const handleSubmit = useCallback(
    async (payload) => {
      try {
        const { description, files, isAnonymous } = payload;

        let postData;
        if (files?.length > 0) {
          postData = new FormData();
          postData.append("description", description);
          postData.append("userId", currentUserId);
          postData.append("isAnonymous", String(isAnonymous));
          files.forEach((file) => postData.append("file", file));
        } else {
          postData = {
            description,
            userId: currentUserId,
            isAnonymous,
            photo: null,
          };
        }

        const result = await handleCreatePost(postData);

        if (result?.success === false) {
          // 🟥 Post refusé
          const fallback = t("medicalQuest.moderation.refusedFallback");
          const msg = result.message || fallback;

          // Ici on affiche le message backend dans le modal
          setModalMessage(`❌ ${msg}`);
          setShowModal(true);
          return false;
        } else if (result?.success === true) {
          // ✅ Post accepté
          message.success(t("medicalQuest.toast.published"));

          // Refresh après un délai
          setTimeout(() => {
            refresh?.();
          }, 800);
          return true;
        }
      } catch {
        message.error(t("medicalQuest.toast.publishFailed"));
        return false;
      }
      return false;
    },
    [currentUserId, handleCreatePost, refresh, t]
  );

  /* ---------------------------------------------------------------------- */
  /* ✏️ Edition d'une question                                              */
  /* ---------------------------------------------------------------------- */
  const handleEdit = useCallback(
    async (id, newText) => {
      try {
        await handleEditPost(id, newText);
        setTimeout(() => refresh(), 400);
      } catch {
        // erreur déjà gérée dans usePosts
      }
    },
    [handleEditPost, refresh]
  );

  /* ---------------------------------------------------------------------- */
  /* 🗑️ Suppression d'une question                                         */
  /* ---------------------------------------------------------------------- */
  const handleDelete = useCallback(
    async (id) => {
      try {
        await dispatch(deletePostByCustomId(id)).unwrap();
        message.success(t("medicalQuest.toast.deleted"));

        // Refresh après un délai
        setTimeout(() => {
          refresh?.();
        }, 500);
      } catch {
        message.error(t("medicalQuest.toast.deleteFailed"));
      }
    },
    [dispatch, refresh, t]
  );

  /* ---------------------------------------------------------------------- */
  /* 🧩 Rendu principal                                                     */
  /* ---------------------------------------------------------------------- */
  return (
    <AppLayout>
      <div className="mqp-wrapper">
        <div className="mqp-grid">
          <ScrollTabs
            activeKey={activeKey}
            onChange={(key) => navigate(key)}
            userId={currentUserId}
            forceActiveKey={ROUTES.PROFILE(currentUserId || ":userId")}
          />

          <main className="mqp-main">
            <AskQuestionBox onSubmit={handleSubmit} />

            {loading ? (
              <div style={{ textAlign: "center", padding: "30px" }}>
                ⏳ {t("medicalQuest.state.loading")}
              </div>
            ) : posts && posts.length > 0 ? (
              <QuestionsList
                items={posts}
                onDelete={handleDelete}
                onEdit={handleEdit}
                currentUserId={currentUserId}
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "#666",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "20px" }}>📭</div>
                <h3>{t("medicalQuest.empty.title")}</h3>
                <p>{t("medicalQuest.empty.subtitle")}</p>
              </div>
            )}
          </main>

          <aside className="mqp-aside">
            <div className="mqp-sticky">
              <FollowedDoctors />
              <MedilinkInfoCard />
            </div>
          </aside>
        </div>
      </div>

      {showModal && (
        <AdminConfirmationModal
          message={modalMessage}
          onClose={() => setShowModal(false)}
        />
      )}
    </AppLayout>
  );
}
